<template>
  <v-row
    class="mx-0 mt-0 mb-4"
    v-if="templateConfig.theme !== 'Operator-Template'"
  >
    <v-col v-if="dataFetched">
      <v-form lazy-validation @submit="verifyPin()" onSubmit="return false;">
        <Alert
          v-if="showError"
          :message="error"
          type="error"
          @toggleAlert="showError = !showError"
        />

        <Alert
          v-if="showSuccess"
          :message="success"
          type="success"
          @toggleAlert="showSuccess = !showSuccess"
        />

        <v-row>
          <v-col>
            <v-text-field
              ref="OTPInput"
              pt-0
              class="centered-input pinCodeInput"
              color="success"
              maxlength="6"
              :disabled="isPinCodeDisabled"
              v-model="pinCode"
              :rules="pincodeRules"
              :label="merchantConfig.texts.pinCodeTextFieldLabel"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <span class="caption codeSendToLbl">
              {{ $translate('CODESENTTO') }}
              {{ SubscribeFlowDetails.checkoutHeaderEnrichmentInfo.msisdn }}
            </span>
            <a @click="resendPin()" class="green--text caption ml-1 mr-1">
              {{ $translate('RESENDPIN') }}
            </a>
            <v-progress-circular
              class="mx-1"
              :width="1"
              :size="15"
              v-if="sendingPinCode"
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn
              elevation="0"
              block
              :disabled="!isButtonActive"
              :loading="loading"
              :rounded="templateConfig.isButtonRounded"
              class="mainButton"
              v-prevent-multiple-clicks:click="verifyPin"
            >
              <span v-html="pinCodeBtnText" v-if="!loading"></span>
            </v-btn>
          </v-col>
        </v-row>
        <SecondaryDisclaimer
          v-if="pinCodeSecondaryDisclaimerText || freeStartDisclaimerText"
        >
          <div class="secondryDisc">
            <div v-html="freeStartDisclaimerText"></div>
            <div v-html="pinCodeSecondaryDisclaimerText"></div>
          </div>
        </SecondaryDisclaimer>
      </v-form>
    </v-col>
    <v-col v-else class="ml-0 mb-4">
      <MainLoader />
    </v-col>
  </v-row>

  <!-- embedded template-->
  <v-row
    class="mx-0 mt-0 mb-4"
    v-else-if="templateConfig.theme == 'Operator-Template'"
  >
    <v-col v-if="dataFetched">
      <v-row class="justify-center">
        <v-col>
          <p>
            {{
              this.$translate(
                this.pinCodeLength
                  ? 'ENTER_PINCODEWITHLENGTH_MSG'
                  : 'ENTER_PINCODE_MSG'
              )
                .replace('{pincodeLength}', this.pinCodeLength)
                .replace(
                  '{mobileNumber}',
                  this.SubscribeFlowDetails.checkoutHeaderEnrichmentInfo.msisdn
                )
            }}
          </p>
        </v-col>
      </v-row>

      <v-form lazy-validation @submit="verifyPin()" onSubmit="return false;">
        <v-row class="justify-center">
          <v-col cols="12" sm="7" md="5" lg="4" xl="3">
            <Alert
              v-if="showError"
              :message="error"
              type="error"
              @toggleAlert="showError = !showError"
            />

            <Alert
              v-if="showSuccess"
              :message="success"
              type="success"
              @toggleAlert="showSuccess = !showSuccess"
            />
          </v-col>
        </v-row>

        <v-row class="justify-center">
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-otp-input
              v-if="pinCodeLength"
              ref="OTPInput"
              class="OTPInput"
              :length="pinCodeLength"
              :disabled="isPinCodeDisabled"
              v-model="pinCode"
              :rules="pincodeRules"
              required
              type="number"
            ></v-otp-input>

            <v-text-field
              v-else
              ref="OTPInput"
              pt-0
              class="centered-input pinCodeInput"
              color="primary"
              maxlength="6"
              :disabled="isPinCodeDisabled"
              v-model="pinCode"
              :rules="pincodeRules"
              :label="merchantConfig.texts.pinCodeTextFieldLabel"
              required
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="justify-center">
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-btn
              elevation="0"
              block
              :disabled="!isButtonActive"
              :loading="loading"
              :rounded="templateConfig.isButtonRounded"
              class="mainButton"
              v-prevent-multiple-clicks:click="verifyPin"
            >
              <span v-html="pinCodeBtnText" v-if="!loading"></span>
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col v-if="showResendPinCode">
            <span>
              {{ $translate('didnotReceiveCodeLbl') }}
            </span>
            <a
              @click="resendPin()"
              class="secondry--text mx-1"
              v-if="!sendingPinCode"
            >
              {{ $translate('RESENDPIN') }}
            </a>
            <v-progress-circular
              class="mx-1"
              :width="1"
              :size="15"
              v-if="sendingPinCode"
              indeterminate
              color="secondry"
            ></v-progress-circular>
          </v-col>

          <v-col v-else-if="showResendPinCodeTimer">
            <span>
              {{ $translate('ResendPinCodeIn') }}
              {{
                new Date(resendPinCodeTimer * 1000)
                  .toISOString()
                  .substring(14, 19)
              }}
            </span>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
    <v-col v-else class="ml-0 mb-4">
      <MainLoader />
    </v-col>
  </v-row>
</template>

<script>
//1.General imports.
import { mapState, mapMutations } from 'vuex';
import { digitsConverter } from '../../../helper/digitsHelper.js';

//2.shared comp. imports.
import MainLoader from '../../Shared/components/MainLoader.vue';
import pincodeRules from '../../Shared/Rules/pincodeRules';
import SecondaryDisclaimer from '../../Shared/components/Disclaimer.vue';
import regex from '../../../constants/regex';
import Alert from '../../Shared/components/Alert.vue';

const RESENDINTERVALTIME = 60;

export default {
  data() {
    return {
      showError: false,
      error: '',
      showSuccess: false,
      success: '',
      isPinCodeDisabled: false,
      pinCode: '',
      pinCodeSmsId: '',
      sessionId: '',
      sendingPinCode: false,
      loading: false,
      resendAttempts: 0,
      dataFetched: false,
      pincodeRules: pincodeRules(this.$translate, regex.PINCODE),
      resendPinCodeTimer: RESENDINTERVALTIME,
      showResendPinCode: true,
      showResendPinCodeTimer: false,
      resendPinCodeInterval: null,
    };
  },
  deactivated() {
    // reset the component before leave
    this.pinCode = '';
    this.$refs.OTPInput.isResetting = true; // access data of the OTPInput component
    this.resetResendPinCodeTimer();
  },
  async activated() {
    await this.resendPin();
    this.dataFetched = true;
  },

  components: { MainLoader, SecondaryDisclaimer, Alert },
  computed: {
    ...mapState(['templateConfig', 'SubscribeFlowDetails', 'merchantConfig']),
    isButtonActive() {
      return this.pincodeRules[0](this.pinCode) === true;
    },
    pinCodeBtnText() {
      //Needs $replacePlaceholders plugin function for [productName, productPrice, currency] placeholders.
      return this.$replacePlaceholders(this.merchantConfig.texts.pinCodeBtn, {
        productName:
          this.SubscribeFlowDetails.subscriptionProductDetails
            .initialProductName,
        price: this.SubscribeFlowDetails.merchantOperatorDisclaimer.price,
        currency: this.SubscribeFlowDetails.merchantOperatorDisclaimer.currency,
      });
    },
    pinCodeSecondaryDisclaimerText() {
      return this.merchantConfig.texts.pinCodeSecondaryDisclaimer;
    },
    freeStartDisclaimerText() {
      //Needs $replacePlaceholders plugin function for [productPrice, currency, frequency, trialPeriod] placeholders.

      if (
        this.SubscribeFlowDetails.subscriptionProductDetails.freeStartPeriod &&
        this.SubscribeFlowDetails.userShouldGetFreeStartPeriod
      ) {
        return this.$replacePlaceholders(
          this.merchantConfig.texts.freeStartDisclaimer,
          {
            price: this.SubscribeFlowDetails.merchantOperatorDisclaimer.price,
            currency:
              this.SubscribeFlowDetails.merchantOperatorDisclaimer.currency,
            frequency:
              this.SubscribeFlowDetails.subscriptionProductDetails.frequency,
            trialPeriod:
              this.SubscribeFlowDetails.subscriptionProductDetails
                .freeTrialDays,
          }
        );
      } else {
        return '';
      }
    },

    pinCodeLength() {
      return this.SubscribeFlowDetails.subscriptionProductDetails.pinCodeLength;
    },
  },
  methods: {
    ...mapMutations(['setSubscribeFlowDetails']),
    async verifyPin() {
      this.loading = true;
      this.isPinCodeDisabled = true;
      this.showError = false;
      this.showSuccess = false;

      this.pinCode = digitsConverter(this.pinCode);
      let response = await this.$http.post(`/HeaderEnrichment/Confirm`, {
        PinCode: this.pinCode,
        PinCodeSmsId: this.pinCodeSmsId,
        SessionId: this.sessionId
      });
      //1.. case success
      if (
        response.data &&
        response.data.enrichementRefNumber &&
        response.data.errorMessage == null
      ) {
        await this.confirmSubscriptionResult();
      }
      //2.. failure case.
      else if (response.data.errorMessage) {
        switch (response.data.errorMessage) {
          case 'Invalid catalog':
          case 'Invalid product':
          case 'Invalid subscription plan':
            this.moveToFailurePage(
              this.$translate('GENERALERRORMESSAGE'), //optional
              '51'
            );
            break;
          case 'Number of trials to enter pincode has been exceeded':
            this.moveToFailurePage(
              this.$translate('INVALIDTRIALSEXCEEDED'), //optional
              '51'
            );
            break;
          case 'Invalid Pin Code':
          case 'Invalid Pincode':
          case 'invalidpin':
            this.showErrorMessage(this.$translate('INVALIDPINCODE'));
            break;
          case 'Pin Code Expired':
            this.moveToFailurePage(
              this.$translate('EXPIREDPINCODE'), //optional
              '51'
            )
            break;
          default:
            this.moveToFailurePage(
              this.$translate('GENERALERRORMESSAGE'), //optional
              '51'
            );
            break;
        }
      }

      this.loading = false;
      this.isPinCodeDisabled = false;
    },

    async confirmSubscriptionResult() {
      this.loading = true;

      let response = await this.$http.post('/sub/SubscribeContract', {
        simulate: process.env.NODE_ENV === 'development' ? true : false,
        sessionToken: this.merchantConfig.sessionToken,
      });
      // let response = {
      //   data: {
      //     charge: true,
      //     errorMessage: null,
      //     msisdn: '201021320396',
      //     nextPaymentDate: '2022-03-02 13:20:15Z',
      //     operationStatusCode: '0',
      //     paymentTransactionStatusCode: '6',
      //     responseCode: 51,
      //     statusCode: 0,
      //     subscriptionContractId: 412783052,
      //     subscriptionContractStatus: '1',
      //     transactionId: '21149053178',
      //     verificationInstruction: null,
      //     verificationMethod: null,
      //   },
      // };
      this.loading = false;
      if (response.data.errorMessage) {
        if (response.data.responseCode === 105) {
          this.moveToFailurePage(
            this.$translate(`RESPONSECODE_${response.data.responseCode}`), //optional
            response.data.responseCode
          );
        } else {
          this.moveToFailurePage(
            this.$translate('GENERALERRORMESSAGE'), //optional
            response.data.operationStatusCode
          );
        }
      } else if (
        response.data.transactionId &&
        response.data.paymentTransactionStatusCode != 0
      ) {
        switch (Number(response.data.paymentTransactionStatusCode)) {
          case 4:
          case 11:
          case 14:
          case 16:
            this.showErrorMessage(
              this.$translate(
                `RESPONSECODE_${response.data.paymentTransactionStatusCode}`
              )
            );
            break;
          case 6:
            this.moveToFailurePage(
              this.$translate('GENERALERRORMESSAGE'), //optional
              response.data.operationStatusCode
            );
            break;
          default:
            this.showErrorMessage(this.$translate('GENERALERRORMESSAGE'));
            break;
        }
      } else {
        this.moveToSuccessPage(response.data.subscriptionContractId);
      }
    },

    async resendPin() {
      this.resendAttempts++; //In HE only "3 trials available".
      if (this.resendAttempts > 3) {
        this.showSuccess = false;
        this.showErrorMessage(this.$translate('RESENDTRIALSEXCEEDED'));
      } else {
        this.sendingPinCode = true;
        this.showError = false;
        this.showSuccess = false;

        let response = await this.$http.post('/HeaderEnrichment/SendPinCode');
        if (response.data.pinCodeSent === false) {
          if (response.data.message) {
            this.showErrorMessage(response.data.message);
          }
        } else if (
          response.data.pinCodeSent === true &&
          response.data.message == null
        ) {
          this.pinCodeSmsId = response.data.pinCodeSmsId;
          this.sessionId = response.data.sessionId;
          //not to show sucess alert for the 1st attempt.
          if (this.resendAttempts !== 1) {
            this.success = this.$translate('PINCODESENTSUCCESS');
            this.showSuccess = true;
          }
        }
        this.sendingPinCode = false;
        this.startResendPinCodeTimer();
      }
    },

    showErrorMessage(msg) {
      this.error = msg;
      this.showError = true;
    },

    moveToSuccessPage(id) {
      this.setSubscribeFlowDetails({
        step: 3,
        component: 'HESuccess',
        paymentId: id,
      });
    },

    moveToFailurePage(msg, operationStatusCode) {
      this.setSubscribeFlowDetails({
        step: 4,
        component: 'HEFailure',
        failureMessage: msg,
        operationStatusCode,
      });
    },

    startResendPinCodeTimer() {
      this.showResendPinCode = false;
      this.showResendPinCodeTimer = true;
      this.resendPinCodeInterval = setInterval(() => {
        this.resendPinCodeTimer--;
        if (this.resendPinCodeTimer == 0) {
          this.resetResendPinCodeTimer();
        }
      }, 1000);
    },

    resetResendPinCodeTimer() {
      this.showResendPinCode = true;
      this.showResendPinCodeTimer = false;
      this.resendPinCodeTimer = RESENDINTERVALTIME;
      clearInterval(this.resendPinCodeInterval);
    },
  },
  // async created() {
  //   await this.resendPin();
  //   this.dataFetched = true;
  // },
};
</script>

<style lang="scss">
.pinCodeInput input[type='text'] {
  transition: color 900000s, background-color 900000s;
}
</style>
